import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Search,
  ArrowUpRight, ArrowDownRight, Download, Edit2, X, Printer,
  ChevronUp, ChevronDown, ChevronsUpDown, CheckSquare, Square, Zap,
  ChevronRight, CreditCard, Loader2, Trash2
} from 'lucide-react'
import { format } from 'date-fns'
import { it } from 'date-fns/locale'
import api from '../services/api'
import { useWorkspace } from '../hooks/useWorkspace'
import { useLanguage } from '../hooks/useLanguage'
import CategoryIcon from '../components/CategoryIcon'
import CategorySelect from '../components/CategorySelect'
import TourGuide, { TourStep } from '../components/TourGuide'

interface Transaction {
  id: string
  date: string
  description: string
  amount: number
  type: 'credit' | 'debit'
  category_id: string
  category_name: string
  cost_type: 'fixed' | 'variable'
  color: string
  upload_id: string
  // SEPA mandate linking
  sepa_mandate?: string
  statement_period?: string
  card_last_four?: string
  sub_transactions_count?: number
  linked_pdf_upload_id?: string
  is_sub_transaction?: boolean
}

interface SubTransaction {
  id: string
  date: string
  description: string
  amount: number
  type: string
  category_id?: string
  category_name?: string
  sepa_mandate?: string
  statement_period?: string
  card_last_four?: string
}

interface Category {
  id: string
  name: string
  icon: string
  color: string
  cost_type: string
}

interface SimilarTransaction {
  id: string
  description: string
  date: string
  amount: number
  type: string
  category_id?: string
  category_name?: string
  matchScore: number
}

type SortField = 'date' | 'description' | 'amount' | 'category_name' | 'cost_type'
type SortDirection = 'asc' | 'desc'

export default function Transactions() {
  const { currentWorkspace } = useWorkspace()
  const { t, language } = useLanguage()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({
    sign: '',
    cost_type: '',
    category_id: '',
    uncategorized_only: false
  })
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null)
  const [sortConfig, setSortConfig] = useState<{ field: SortField; direction: SortDirection }>({ field: 'date', direction: 'desc' })
  
  // Multi-selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showBulkModal, setShowBulkModal] = useState(false)
  
  // Similar transactions state
  const [similarTransactions, setSimilarTransactions] = useState<SimilarTransaction[]>([])
  const [showSimilarModal, setShowSimilarModal] = useState(false)
  const [pendingCategory, setPendingCategory] = useState<Category | null>(null)

  // Sub-transactions drill-down state
  const [expandedTransactionId, setExpandedTransactionId] = useState<string | null>(null)
  const [subTransactions, setSubTransactions] = useState<SubTransaction[]>([])
  const [loadingSubTransactions, setLoadingSubTransactions] = useState(false)

  // Fetch categories (include workspace ID to reload when workspace changes)
  const { data: categoriesData } = useQuery({
    queryKey: ['categories', currentWorkspace?.id],
    queryFn: async () => {
      const response = await api.get('/categories/')
      console.log('Categories response:', response.data)
      return response.data as Category[]
    },
    enabled: !!currentWorkspace?.id
  })

  const categories = Array.isArray(categoriesData) ? categoriesData : []

  // Mutation to update transaction category
  const updateMutation = useMutation({
    mutationFn: async ({ id, category_id, cost_type }: { id: string; category_id: string; cost_type: string }) => {
      await api.patch(`/transactions/${id}`, { category_id, cost_type })
    },
    onSuccess: () => {
      setEditingTransaction(null)
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
    }
  })

  // Mutation to update transaction amount (entrata/uscita)
  const updateAmountMutation = useMutation({
    mutationFn: async ({ id, amount }: { id: string; amount: number }) => {
      await api.patch(`/transactions/${id}`, { amount })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    }
  })

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: async ({ transaction_ids, category_id, cost_type }: { transaction_ids: string[]; category_id: string; cost_type?: string }) => {
      const response = await api.post('/transactions/bulk', { transaction_ids, category_id, cost_type })
      return response.data
    },
    onSuccess: (data) => {
      setShowBulkModal(false)
      setSelectedIds(new Set())
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      alert(`${data.updated_count} transazioni aggiornate a "${data.category}"`)
    }
  })

  // Delete transaction mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/transactions/${id}`)
    },
    onSuccess: () => {
      setEditingTransaction(null)
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    }
  })

  // Find similar transactions
  const findSimilarMutation = useMutation({
    mutationFn: async ({ description, exclude_transaction_id }: { description: string; exclude_transaction_id?: string }) => {
      const response = await api.post('/transactions/similar', { description, exclude_transaction_id })
      return response.data
    },
    onSuccess: (data) => {
      if (data.similar && data.similar.length > 0) {
        setSimilarTransactions(data.similar)
        setShowSimilarModal(true)
      }
    }
  })

  // Load sub-transactions for drill-down
  const loadSubTransactions = async (transactionId: string) => {
    if (expandedTransactionId === transactionId) {
      // Collapse if already expanded
      setExpandedTransactionId(null)
      setSubTransactions([])
      return
    }
    
    setLoadingSubTransactions(true)
    setExpandedTransactionId(transactionId)
    
    try {
      const response = await api.get(`/transactions/${transactionId}/sub-transactions`)
      setSubTransactions(response.data.sub_transactions || [])
    } catch (error) {
      console.error('Failed to load sub-transactions:', error)
      setSubTransactions([])
    } finally {
      setLoadingSubTransactions(false)
    }
  }

  const handleCategoryChange = async (category: Category) => {
    if (!editingTransaction) return
    
    // First, find similar transactions
    const result = await findSimilarMutation.mutateAsync({
      description: editingTransaction.description,
      exclude_transaction_id: editingTransaction.id
    })
    
    if (result.similar && result.similar.length > 0 && result.uncategorized_count > 0) {
      // Show similar transactions modal
      setPendingCategory(category)
      setSimilarTransactions(result.similar)
      setShowSimilarModal(true)
    } else {
      // No similar transactions, just update this one
      updateMutation.mutate({
        id: editingTransaction.id,
        category_id: category.id,
        cost_type: category.cost_type
      })
    }
  }

  const handleApplyToSimilar = (applyToAll: boolean) => {
    if (!pendingCategory || !editingTransaction) return
    
    if (applyToAll) {
      // Get uncategorized similar transaction IDs + the current one
      const uncategorizedIds = similarTransactions
        .filter(t => !t.category_id || t.category_id === 'altro' || t.category_id === 'other')
        .map(t => t.id)
      const allIds = [editingTransaction.id, ...uncategorizedIds]
      
      bulkUpdateMutation.mutate({
        transaction_ids: allIds,
        category_id: pendingCategory.id,
        cost_type: pendingCategory.cost_type
      })
    } else {
      // Just update the current transaction
      updateMutation.mutate({
        id: editingTransaction.id,
        category_id: pendingCategory.id,
        cost_type: pendingCategory.cost_type
      })
    }
    
    setShowSimilarModal(false)
    setPendingCategory(null)
    setSimilarTransactions([])
    setEditingTransaction(null)
  }

  // Selection helpers
  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const selectAll = () => {
    if (selectedIds.size === sortedTransactions.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(sortedTransactions.map((tx: Transaction) => tx.id)))
    }
  }

  const handleBulkCategoryChange = (category: Category) => {
    bulkUpdateMutation.mutate({
      transaction_ids: Array.from(selectedIds),
      category_id: category.id,
      cost_type: category.cost_type
    })
  }

  // Fetch transactions
  const { data, isLoading } = useQuery({
    queryKey: ['transactions', page, search, filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '20',
        ...(search && { search }),
        ...(filters.sign && { sign: filters.sign }),
        ...(filters.cost_type && { cost_type: filters.cost_type }),
        ...(filters.category_id && { category_id: filters.category_id }),
        ...(filters.uncategorized_only && { uncategorized_only: 'true' })
      })
      const response = await api.get(`/transactions/?${params}`)
      return response.data
    }
  })

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value)

  const rawTransactions = data?.data
  const totalPages = data?.pagination?.total_pages || 1

  // Sort transactions
  const sortedTransactions = useMemo(() => {
    const transactions = rawTransactions || []
    if (!transactions.length) return transactions
    
    return [...transactions].sort((a: Transaction, b: Transaction) => {
      let aValue: string | number = a[sortConfig.field]
      let bValue: string | number = b[sortConfig.field]
      
      // Handle special cases
      if (sortConfig.field === 'amount') {
        aValue = Math.abs(a.amount)
        bValue = Math.abs(b.amount)
      } else if (sortConfig.field === 'date') {
        aValue = new Date(a.date).getTime()
        bValue = new Date(b.date).getTime()
      } else {
        aValue = String(aValue || '').toLowerCase()
        bValue = String(bValue || '').toLowerCase()
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
  }, [rawTransactions, sortConfig])

  // Toggle sort
  const handleSort = (field: SortField) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }))
  }

  // Render sort icon
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="w-4 h-4 text-gray-400" />
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-primary-500" />
      : <ChevronDown className="w-4 h-4 text-primary-500" />
  }

  // Export function - exports ALL filtered transactions (not just current page)
  const handleExport = async () => {
    try {
      // Fetch all transactions with current filters (no pagination limit)
      const params = new URLSearchParams({
        per_page: '10000', // Get all
        ...(search && { search }),
        ...(filters.sign && { sign: filters.sign }),
        ...(filters.cost_type && { cost_type: filters.cost_type }),
        ...(filters.category_id && { category_id: filters.category_id }),
        ...(filters.uncategorized_only && { uncategorized_only: 'true' })
      })
      const response = await api.get(`/transactions/?${params}`)
      const allTransactions = response.data.data || []

      if (allTransactions.length === 0) {
        alert('Nessuna transazione da esportare')
        return
      }

      // Build CSV content
      const headers = ['Data', 'Descrizione', 'Importo', 'Tipo', 'Categoria', 'Tipo Costo']
      const rows: string[][] = allTransactions.map((tx: Transaction) => [
        tx.date,
        `"${tx.description.replace(/"/g, '""')}"`, // Escape quotes
        tx.amount.toString().replace('.', ','), // Italian decimal
        tx.type === 'credit' ? 'Entrata' : 'Uscita',
        tx.category_name || '',
        tx.cost_type === 'fixed' ? 'Fisso' : tx.cost_type === 'variable' ? 'Variabile' : ''
      ])

      const csvContent = [
        headers.join(';'),
        ...rows.map((row: string[]) => row.join(';'))
      ].join('\n')

      // Add BOM for Excel compatibility with Italian characters
      const BOM = '\uFEFF'
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
      
      // Create download link
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Filename with date and filter info
      const dateStr = new Date().toISOString().split('T')[0]
      let filename = `transazioni_${dateStr}`
      if (filters.category_id) filename += `_${filters.category_id}`
      if (filters.sign) filename += `_${filters.sign}`
      if (filters.cost_type) filename += `_${filters.cost_type}`
      filename += '.csv'
      
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export error:', error)
      alert('Errore durante l\'esportazione')
    }
  }

  // Tour steps for Transactions
  const tourSteps: TourStep[] = language === 'it' ? [
    {
      title: 'Benvenuto in Transazioni! 📊',
      content: 'Qui puoi visualizzare, cercare e gestire tutte le tue transazioni bancarie importate.',
      position: 'center'
    },
    {
      target: '[data-tour="search-box"]',
      title: 'Ricerca Transazioni',
      content: 'Cerca transazioni per descrizione, importo o qualsiasi altro testo. La ricerca è istantanea!',
      position: 'bottom'
    },
    {
      target: '[data-tour="filters"]',
      title: 'Filtri Avanzati',
      content: 'Filtra per tipo (entrate/uscite), categoria, costi fissi/variabili o visualizza solo le transazioni da categorizzare.',
      position: 'bottom'
    },
    {
      target: '[data-tour="transaction-table"]',
      title: 'Tabella Transazioni',
      content: 'Clicca sulle intestazioni per ordinare. Clicca sulla matita per modificare categoria e tipo di costo.',
      position: 'top'
    }
  ] : [
    {
      title: 'Welcome to Transactions! 📊',
      content: 'Here you can view, search and manage all your imported bank transactions.',
      position: 'center'
    },
    {
      target: '[data-tour="search-box"]',
      title: 'Search Transactions',
      content: 'Search transactions by description, amount or any other text. Search is instant!',
      position: 'bottom'
    },
    {
      target: '[data-tour="filters"]',
      title: 'Advanced Filters',
      content: 'Filter by type (income/expenses), category, fixed/variable costs or show only uncategorized transactions.',
      position: 'bottom'
    },
    {
      target: '[data-tour="transaction-table"]',
      title: 'Transaction Table',
      content: 'Click on headers to sort. Click the pencil to edit category and cost type.',
      position: 'top'
    }
  ]

  return (
    <>
      <TourGuide tourId="transactions" steps={tourSteps} />
      <div className="space-y-6 pt-1">
        {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            {language === 'it' ? 'Carte Aziendali' : 'Company Cards'}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
            {data?.pagination?.total || 0} {language === 'it' ? 'transazioni totali' : 'total transactions'}
          </p>
        </div>
        
        {/* Print and Export Buttons */}
        <div className="flex gap-2 no-print">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
          >
            <Printer className="w-4 h-4" />
            {t.dashboard.print}
          </button>
          <button
            onClick={() => handleExport()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" />
            {language === 'it' ? 'Esporta CSV' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Search and Filters - inline full width */}
      <div className="card">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          {/* Search */}
          <div data-tour="search-box" className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              placeholder={t.transactions.search}
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg w-full bg-white dark:bg-slate-800 text-gray-900 dark:text-white
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            />
          </div>

          {/* Divider */}
          <div className="hidden sm:block w-px h-8 bg-gray-200 dark:bg-slate-600" />

          {/* Direction Filter */}
          <select
            data-tour="filters"
            value={filters.sign}
            onChange={(e) => setFilters({ ...filters, sign: e.target.value })}
            className="border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white flex-1 min-w-[100px]"
          >
            <option value="">Direzione</option>
            <option value="debit">Uscite</option>
            <option value="credit">Entrate</option>
          </select>

          {/* Cost Type Filter */}
          <select
            value={filters.cost_type}
            onChange={(e) => setFilters({ ...filters, cost_type: e.target.value })}
            className="border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white flex-1 min-w-[100px]"
          >
            <option value="">Tipo costo</option>
            <option value="fixed">Fissi</option>
            <option value="variable">Variabili</option>
          </select>

          {/* Category Filter */}
          <CategorySelect
            value={filters.category_id}
            onChange={(value) => setFilters({ ...filters, category_id: value })}
            categories={categories}
            placeholder="Categoria"
            className="flex-1 min-w-[140px] max-w-[200px]"
          />

          {/* Uncategorized checkbox */}
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
            <input
              type="checkbox"
              checked={filters.uncategorized_only}
              onChange={(e) => setFilters({ ...filters, uncategorized_only: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Solo non categorizzate
          </label>

          {/* Reset filters button */}
          {(filters.sign || filters.cost_type || filters.category_id || filters.uncategorized_only || search) && (
            <button
              onClick={() => {
                setFilters({ sign: '', cost_type: '', category_id: '', uncategorized_only: false })
                setSearch('')
              }}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 underline whitespace-nowrap"
            >
              Azzera filtri
            </button>
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="sticky top-0 z-10 bg-primary-600 text-white rounded-lg p-3 flex items-center justify-between shadow-elevated">
          <div className="flex items-center gap-3">
            <CheckSquare className="w-5 h-5" />
            <span className="font-medium">{selectedIds.size} transazioni selezionate</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBulkModal(true)}
              className="flex items-center gap-2 h-[44px] px-4 bg-white text-primary-600 rounded-lg hover:bg-gray-100 hover:-translate-y-px active:scale-[0.98] transition-all duration-200 ease-apple font-medium"
            >
              <Zap className="w-4 h-4" />
              Modifica categoria
            </button>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="px-3 py-2 hover:bg-primary-700 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Transactions Table */}
      <div data-tour="transaction-table" className="card p-0 overflow-hidden">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th className="w-10">
                  <button
                    onClick={selectAll}
                    className="p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
                  >
                    {selectedIds.size === sortedTransactions.length && sortedTransactions.length > 0 ? (
                      <CheckSquare className="w-4 h-4 text-primary-500" />
                    ) : (
                      <Square className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </th>
                <th 
                  onClick={() => handleSort('date')} 
                  className="cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 select-none"
                >
                  <div className="flex items-center gap-1">
                    Data
                    <SortIcon field="date" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('description')} 
                  className="cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 select-none"
                >
                  <div className="flex items-center gap-1">
                    Descrizione
                    <SortIcon field="description" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('amount')} 
                  className="cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 select-none text-right"
                >
                  <div className="flex items-center gap-1 justify-end">
                    Importo
                    <SortIcon field="amount" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('category_name')} 
                  className="cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 select-none"
                >
                  <div className="flex items-center gap-1">
                    Categoria
                    <SortIcon field="category_name" />
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('cost_type')} 
                  className="cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 select-none"
                >
                  <div className="flex items-center gap-1">
                    Tipo
                    <SortIcon field="cost_type" />
                  </div>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-8">
                    <div className="spinner mx-auto" />
                  </td>
                </tr>
              ) : sortedTransactions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Nessuna transazione trovata
                  </td>
                </tr>
              ) : (
                sortedTransactions.map((tx: Transaction) => (
                  <>
                  <tr key={tx.id} className={`hover:bg-black/[0.02] dark:hover:bg-white/[0.02] ${selectedIds.has(tx.id) ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}>
                    <td className="w-10">
                      <button
                        onClick={() => toggleSelection(tx.id)}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
                      >
                        {selectedIds.has(tx.id) ? (
                          <CheckSquare className="w-4 h-4 text-primary-500" />
                        ) : (
                          <Square className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                    </td>
                    <td className="whitespace-nowrap">
                      <span className="text-gray-900 dark:text-gray-100">
                        {format(new Date(tx.date), 'd MMM yyyy', { locale: it })}
                      </span>
                    </td>
                    <td className="min-w-[300px]">
                      <div className="max-w-xl">
                        <p className="text-gray-900 dark:text-gray-100 font-medium text-xs break-words">
                          {tx.description || 'Nessuna descrizione'}
                        </p>
                        {/* Sub-transactions indicator */}
                        {(tx.sub_transactions_count && tx.sub_transactions_count > 0) || tx.linked_pdf_upload_id || tx.sepa_mandate ? (
                          <button
                            onClick={() => loadSubTransactions(tx.id)}
                            className="mt-1 flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                          >
                            {loadingSubTransactions && expandedTransactionId === tx.id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <ChevronRight className={`w-3 h-3 transition-transform ${expandedTransactionId === tx.id ? 'rotate-90' : ''}`} />
                            )}
                            <CreditCard className="w-3 h-3" />
                            <span>
                              {tx.sub_transactions_count ? `${tx.sub_transactions_count} movimenti carta` : 'Vedi dettaglio carta'}
                            </span>
                            {tx.card_last_four && <span className="text-gray-400">•••• {tx.card_last_four}</span>}
                          </button>
                        ) : null}
                      </div>
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        {tx.type === 'credit' ? (
                          <ArrowUpRight className="w-4 h-4 text-green-500" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4 text-red-500" />
                        )}
                        <span className={`font-semibold ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                          {tx.type === 'credit' ? '+' : '-'}{formatCurrency(Math.abs(tx.amount))}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span 
                        className="text-xs font-medium px-2 py-1 rounded-full"
                        style={{ 
                          backgroundColor: `${tx.color}20`,
                          color: tx.color
                        }}
                      >
                        {tx.category_name}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${
                        tx.cost_type === 'fixed' ? 'badge-fixed' : 'badge-variable'
                      }`}>
                        {tx.cost_type === 'fixed' ? 'Fisso' : 'Variabile'}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => setEditingTransaction(tx)}
                        className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                        title="Modifica categoria"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                  {/* Sub-transactions expanded view */}
                  {expandedTransactionId === tx.id && subTransactions.length > 0 && (
                    <tr key={`${tx.id}-sub`}>
                      <td colSpan={7} className="bg-gray-50 dark:bg-slate-800 p-0">
                        <div className="pl-12 py-2 border-l-4 border-primary-500">
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 flex items-center gap-2">
                            <CreditCard className="w-4 h-4" />
                            Dettaglio movimenti carta {tx.card_last_four && `•••• ${tx.card_last_four}`}
                            {tx.statement_period && <span className="text-gray-400">| Periodo: {tx.statement_period}</span>}
                          </div>
                          <div className="space-y-1 max-h-64 overflow-y-auto">
                            {subTransactions.map((sub, idx) => (
                              <div 
                                key={sub.id || idx}
                                className="flex items-center justify-between py-1.5 px-3 bg-white dark:bg-slate-700 rounded text-sm"
                              >
                                <div className="flex items-center gap-3">
                                  <span className="text-gray-500 dark:text-gray-400 text-xs">
                                    {format(new Date(sub.date), 'd MMM', { locale: it })}
                                  </span>
                                  <span className="text-gray-900 dark:text-gray-100">
                                    {sub.description}
                                  </span>
                                </div>
                                <span className={`font-medium ${sub.amount < 0 || sub.type === 'debit' ? 'text-red-600' : 'text-green-600'}`}>
                                  {sub.amount < 0 || sub.type === 'debit' ? '-' : '+'}{formatCurrency(Math.abs(sub.amount))}
                                </span>
                              </div>
                            ))}
                          </div>
                          <div className="mt-2 pt-2 border-t border-gray-200 dark:border-slate-600 flex justify-between text-sm">
                            <span className="text-gray-500 dark:text-gray-400">{subTransactions.length} movimenti</span>
                            <span className="font-semibold text-gray-900 dark:text-gray-100">
                              Totale: {formatCurrency(subTransactions.reduce((sum, s) => sum + Math.abs(s.amount), 0))}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-slate-700">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn btn-secondary text-sm disabled:opacity-50"
            >
              Precedente
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Pagina {page} di {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="btn btn-secondary text-sm disabled:opacity-50"
            >
              Successiva
            </button>
          </div>
        )}
      </div>

      {/* Edit Category Modal */}
      {editingTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-modal w-full max-w-lg mx-4 overflow-hidden animate-scaleIn">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Modifica Categoria</h3>
              <button 
                onClick={() => setEditingTransaction(null)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Transaction Info */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-slate-700 rounded-xl">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                  {format(new Date(editingTransaction.date), 'd MMM yyyy', { locale: it })}
                </p>
                <p className="text-gray-900 dark:text-white font-medium mb-2">{editingTransaction.description}</p>
                
                {/* Amount with toggle */}
                <div className="flex items-center gap-3 mb-3">
                  <p className={`text-sm font-bold ${editingTransaction.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(editingTransaction.amount)}
                  </p>
                  
                  {/* Toggle Entrata/Uscita */}
                  <div className="flex bg-gray-200 dark:bg-slate-600 rounded-lg p-0.5">
                    <button
                      onClick={() => {
                        const newAmount = Math.abs(editingTransaction.amount)
                        setEditingTransaction({ ...editingTransaction, amount: newAmount, type: 'credit' })
                        updateAmountMutation.mutate({ id: editingTransaction.id, amount: newAmount })
                      }}
                      disabled={updateAmountMutation.isPending}
                      className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium transition-all ${
                        editingTransaction.amount >= 0 
                          ? 'bg-green-500 text-white shadow' 
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-slate-500'
                      }`}
                    >
                      <ArrowUpRight className="w-3 h-3" />
                      Entrata
                    </button>
                    <button
                      onClick={() => {
                        const newAmount = -Math.abs(editingTransaction.amount)
                        setEditingTransaction({ ...editingTransaction, amount: newAmount, type: 'debit' })
                        updateAmountMutation.mutate({ id: editingTransaction.id, amount: newAmount })
                      }}
                      disabled={updateAmountMutation.isPending}
                      className={`flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium transition-all ${
                        editingTransaction.amount < 0 
                          ? 'bg-red-500 text-white shadow' 
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-slate-500'
                      }`}
                    >
                      <ArrowDownRight className="w-3 h-3" />
                      Uscita
                    </button>
                  </div>
                  
                  {updateAmountMutation.isPending && (
                    <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
                  )}
                </div>
                
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Categoria attuale: <span className="font-medium">{editingTransaction.category_name}</span>
                </p>
              </div>

              {/* Category Selection */}
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Seleziona nuova categoria:</p>
              <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                {categories.filter(c => c.id !== 'altro' && c.id !== 'other').map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryChange(category)}
                    disabled={updateMutation.isPending}
                    className={`flex items-center gap-2 p-3 rounded-lg border hover:border-gray-400 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-700 transition-all text-left ${
                      editingTransaction.category_id === category.id ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/30' : 'border-gray-200 dark:border-slate-600'
                    }`}
                    style={{ borderLeftColor: category.color, borderLeftWidth: '4px' }}
                  >
                    <CategoryIcon icon={category.icon} className="w-5 h-5" style={{ color: category.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{category.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {category.cost_type === 'fixed' ? 'Fisso' : category.cost_type === 'variable' ? 'Variabile' : 'Entrata'}
                      </p>
                    </div>
                  </button>
                ))}
              </div>

              {(updateMutation.isPending || findSimilarMutation.isPending || deleteMutation.isPending) && (
                <div className="mt-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {findSimilarMutation.isPending ? 'Ricerca transazioni simili...' : deleteMutation.isPending ? 'Eliminazione...' : 'Aggiornamento...'}
                  </p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-slate-700 border-t border-gray-100 dark:border-slate-600 flex gap-3">
              <button
                onClick={() => {
                  if (confirm('Sei sicuro di voler eliminare questa transazione?')) {
                    deleteMutation.mutate(editingTransaction.id)
                  }
                }}
                disabled={deleteMutation.isPending}
                className="flex items-center justify-center gap-2 h-[44px] px-4 text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/40 font-medium hover:-translate-y-px active:scale-[0.98] transition-all duration-200 ease-apple"
              >
                <Trash2 className="w-4 h-4" />
                Elimina
              </button>
              <button
                onClick={() => setEditingTransaction(null)}
                className="flex-1 h-[44px] px-4 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-600 border border-gray-300 dark:border-slate-500 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-500 font-medium hover:-translate-y-px active:scale-[0.98] transition-all duration-200 ease-apple"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Category Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-modal w-full max-w-lg mx-4 overflow-hidden animate-scaleIn">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Modifica {selectedIds.size} transazioni
              </h3>
              <button 
                onClick={() => setShowBulkModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Seleziona una categoria da applicare a tutte le transazioni selezionate:
              </p>
              
              <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                {categories.filter(c => c.id !== 'altro' && c.id !== 'other').map((category) => (
                  <button
                    key={category.id}
                    onClick={() => handleBulkCategoryChange(category)}
                    disabled={bulkUpdateMutation.isPending}
                    className="flex items-center gap-2 p-3 rounded-lg border border-gray-200 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-700 transition-all text-left"
                    style={{ borderLeftColor: category.color, borderLeftWidth: '4px' }}
                  >
                    <CategoryIcon icon={category.icon} className="w-5 h-5" style={{ color: category.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{category.name}</p>
                    </div>
                  </button>
                ))}
              </div>

              {bulkUpdateMutation.isPending && (
                <div className="mt-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Aggiornamento in corso...</p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-slate-700 border-t border-gray-100 dark:border-slate-600">
              <button
                onClick={() => setShowBulkModal(false)}
                className="w-full h-[44px] px-4 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-600 border border-gray-300 dark:border-slate-500 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-500 font-medium hover:-translate-y-px active:scale-[0.98] transition-all duration-200 ease-apple"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Similar Transactions Modal */}
      {showSimilarModal && pendingCategory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-modal w-full max-w-2xl mx-4 overflow-hidden animate-scaleIn">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-slate-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Transazioni simili trovate!
              </h3>
              <button 
                onClick={() => {
                  setShowSimilarModal(false)
                  setPendingCategory(null)
                  setSimilarTransactions([])
                }}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-xl border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Ho trovato <strong>{similarTransactions.length}</strong> transazioni simili a questa.
                  {similarTransactions.filter(t => !t.category_id || t.category_id === 'altro').length > 0 && (
                    <> Di queste, <strong>{similarTransactions.filter(t => !t.category_id || t.category_id === 'altro').length}</strong> non sono ancora categorizzate.</>
                  )}
                </p>
                <p className="text-sm text-blue-800 dark:text-blue-200 mt-2">
                  Vuoi assegnare la categoria <strong style={{ color: pendingCategory.color }}>{pendingCategory.name}</strong> a tutte?
                </p>
              </div>

              {/* Preview of similar transactions */}
              <div className="max-h-48 overflow-y-auto space-y-2 mb-4">
                {similarTransactions.slice(0, 10).map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-slate-700 rounded-lg text-sm">
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 dark:text-white truncate">{tx.description}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{tx.date}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={tx.amount < 0 ? 'text-red-600' : 'text-green-600'}>
                        €{Math.abs(tx.amount).toFixed(2)}
                      </span>
                      {tx.category_name && (
                        <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-slate-600 rounded">
                          {tx.category_name}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {similarTransactions.length > 10 && (
                  <p className="text-xs text-gray-500 text-center">
                    ... e altre {similarTransactions.length - 10} transazioni
                  </p>
                )}
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-slate-700 border-t border-gray-100 dark:border-slate-600 flex gap-3">
              <button
                onClick={() => handleApplyToSimilar(false)}
                disabled={bulkUpdateMutation.isPending}
                className="flex-1 h-[44px] px-4 text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-600 border border-gray-300 dark:border-slate-500 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-500 font-medium hover:-translate-y-px active:scale-[0.98] transition-all duration-200 ease-apple"
              >
                Solo questa
              </button>
              <button
                onClick={() => handleApplyToSimilar(true)}
                disabled={bulkUpdateMutation.isPending}
                className="flex-1 h-[44px] px-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 hover:-translate-y-px hover:shadow-btn-hover active:scale-[0.98] transition-all duration-200 ease-apple font-medium flex items-center justify-center gap-2"
              >
                {bulkUpdateMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    Applica a tutte ({similarTransactions.filter(t => !t.category_id || t.category_id === 'altro').length + 1})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  )
}
